import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ContactsSidePanelData } from './types'
import { Size as BodyTextSize } from '../../../atoms/bodyText/BodyText'
import { CheckIcon } from '../../../atoms/icons/CheckIcon'
import { Link } from '../../../atoms/link/Link'
import { Tooltip } from '../../../atoms/tooltip/Tooltip'
import { openUrl } from '../../../utils/expo-linking'
import { tID } from '../../../utils/utils'
import { ClientDetailsSidePanelDetail, ClientDetailsSidePanelDetails } from '../ClientDetailsSidePanelDetails'
import { ClientDetailsSidePanelSection } from '../ClientDetailsSidePanelSection'
import { contactsSidePanelMessages } from './ContactsSidePanel.messages'

export type ContactsSidePanelSectionProps = {
  data?: ContactsSidePanelData
  clientFirstName?: string
}

const PhoneValueRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const onContactInformationPressed = (phoneNumberOrEmail: string) => {
  const isEmail = phoneNumberOrEmail.indexOf('@') >= 0
  openUrl(isEmail ? `mailto:${phoneNumberOrEmail}` : `tel:${phoneNumberOrEmail}`)
}

export const ContactsSidePanelSection: FunctionComponent<ContactsSidePanelSectionProps> = ({
  data,
  clientFirstName,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const guardian = data?.guardian

  if (!guardian?.firstName && !guardian?.lastName && !guardian?.phone && !guardian?.email) {
    return null
  }

  const items: ClientDetailsSidePanelDetail[] = []

  if (guardian.phone) {
    const phone = guardian.phone
    items.push({
      id: 'phone',
      label: formatMessage(contactsSidePanelMessages.phone),
      value: (
        <PhoneValueRow>
          <Link
            testID={tID('Contacts-phone-link')}
            text={phone}
            onPress={() => onContactInformationPressed(phone)}
            size={BodyTextSize.SMALL}
          />
          {guardian.canLeaveVoicemail && (
            <Tooltip
              placement='top'
              content={formatMessage(contactsSidePanelMessages.voicemailConsent, { clientFirstName })}
              testID={tID('Contacts-voicemail-tooltip')}
            >
              <CheckIcon fillColor={colors.iconSuccess} size={24} />
            </Tooltip>
          )}
        </PhoneValueRow>
      ),
    })
  }

  if (guardian.email) {
    const email = guardian.email
    items.push({
      id: 'email',
      label: formatMessage(contactsSidePanelMessages.email),
      value: (
        <Link
          testID={tID('Contacts-email-link')}
          text={email}
          onPress={() => onContactInformationPressed(email)}
          size={BodyTextSize.SMALL}
        />
      ),
    })
  }

  const subtitle = formatMessage(contactsSidePanelMessages.subtitle)
  const fullName = [guardian.firstName, guardian.lastName].filter(Boolean).join(' ')
  const title = formatMessage(contactsSidePanelMessages.collapsedTitle, { name: fullName })

  return (
    <ClientDetailsSidePanelSection
      subtitle={subtitle}
      accessibilityName={subtitle}
      header={
        items.length > 0
          ? { kind: 'collapsible', collapsedTitle: title, expandedTitle: title }
          : { kind: 'static', title }
      }
      content={
        items.length > 0 ? (
          <ClientDetailsSidePanelDetails items={items} testIDPrefix='Contacts' showTopDivider={false} />
        ) : undefined
      }
      testIDPrefix='Contacts'
    />
  )
}
